import React, { useState, useEffect } from 'react';
import { X, Save, ChevronDown, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';

const RoleEditorModal = ({ isOpen, onClose, role, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [featureFlags, setFeatureFlags] = useState({});

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Permission structure with labels
  const permissionLabels = {
    dashboards: {
      label: 'Dashboards',
      description: 'Access to various dashboard views',
      items: {
        hospital: 'Hospital Dashboard',
        web_analytics: 'Web Analytics',
        popup_analytics: 'Popup Analytics',
        employee: 'Employee Dashboard'
      }
    },
    hr: {
      label: 'Human Resources',
      description: 'HR management features',
      hasEnabled: true,
      items: {
        paystubs: 'Paystubs',
        employees: 'Employees',
        timesheets: 'Timesheets'
      }
    },
    records: {
      label: 'Records',
      description: 'Client and hospital records',
      hasEnabled: true,
      items: {
        client: 'Client Records',
        hospital: 'Hospital Records'
      }
    },
    website: {
      label: 'Website',
      description: 'Website content management',
      items: {
        forms: 'Forms',
        photos: 'Photos',
        reviews: 'Reviews',
        business_hours: 'Business Hours',
        business_details: 'Business Details',
        landing_page_popups: 'Landing Page Popups'
      }
    },
    business: {
      label: 'Business Tools',
      description: 'Business utilities',
      items: {
        pdfs: 'PDFs',
        assets: 'Assets',
        invoices: 'Invoices',
        qr_codes: 'QR Codes',
        short_urls: 'Short URLs',
        employee_schedules: 'Employee Schedules'
      }
    },
    system_settings: {
      label: 'System Settings',
      description: 'System configuration',
      hasEnabled: true,
      items: {
        cms: 'CMS Settings',
        sms: 'SMS Configuration',
        email: 'Email Configuration',
        users: 'User Management',
        payments: 'Payment Settings',
        manage_roles: 'Change Employee Roles'
      }
    },
    online_ordering: {
      label: 'Online Ordering',
      description: 'E-commerce features',
      hasEnabled: true,
      items: {
        order_online: 'Order Online',
        manage_orders: 'Manage Orders',
        pending_orders: 'Pending Orders',
        store_settings: 'Store Settings',
        manage_products: 'Manage Products',
        configure_order_types: 'Configure Order Types'
      }
    },
    online_appointments: {
      label: 'Online Appointments',
      description: 'Appointment booking system',
      hasEnabled: true,
      subSections: {
        services: {
          label: 'Services',
          items: {
            book_online: 'Book Online',
            manage_services: 'Manage Services',
            manage_appointments: 'Manage Appointments',
            configure_service_providers: 'Configure Service Providers'
          }
        },
        boarding: {
          label: 'Boarding',
          items: {
            book_online: 'Book Online',
            configure_boarding: 'Configure Boarding'
          }
        },
        urgent_care: {
          label: 'Urgent Care',
          items: {
            book_online: 'Book Online',
            manage_appointments: 'Manage Appointments'
          }
        }
      }
    },
    ai_features: {
      label: 'AI Features',
      description: 'AI-powered tools',
      hasEnabled: true,
      items: {
        dashboard: 'AI Dashboard',
        agent_config: 'Agent Config',
        ready_to_publish: 'Ready to Publish',
        published_posts: 'Published Posts',
        in_review: 'In Review',
        settings: 'AI Settings',
        costs: 'AI Costs',
        chatbot: 'AI Chatbot'
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (role) {
        // Editing existing role
        setFormData({
          name: role.name || '',
          description: role.description || '',
          permissions: role.permissions || {}
        });
      } else {
        // Creating new role - initialize with default permissions structure
        fetchPermissionStructure();
      }
      // Close all sections by default
      setExpandedSections({});
      // Fetch feature flags
      fetchFeatureFlags();
    }
  }, [isOpen, role]);

  const fetchFeatureFlags = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cms-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFeatureFlags({
          urgent_care_enabled: data.urgent_care_enabled !== false,
          online_ordering_enabled: data.online_ordering_enabled !== false,
          online_appointments_enabled: data.online_appointments_enabled !== false,
          services_enabled: data.services_enabled !== false,
          boarding_enabled: data.boarding_enabled !== false,
          records_enabled: data.records_enabled !== false,
          hr_enabled: data.hr_enabled !== false,
          ai_costs_enabled: data.ai_costs_enabled !== false,
          system_settings_enabled: data.system_settings_enabled !== false
        });
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  };

  const fetchPermissionStructure = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/permissions/structure`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          permissions: data.structure || {}
        }));
      }
    } catch (error) {
      console.error('Error fetching permission structure:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.name.trim()) {
        setError('Role name is required');
        return;
      }

      const url = role
        ? `${API_BASE_URL}/api/roles/${role.id}`
        : `${API_BASE_URL}/api/roles`;
      
      const method = role ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save role');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updatePermission = (path, value) => {
    setFormData(prev => {
      const newPermissions = { ...prev.permissions };
      const pathArray = path.split('.');
      let current = newPermissions;
      
      for (let i = 0; i < pathArray.length - 1; i++) {
        if (!current[pathArray[i]]) {
          current[pathArray[i]] = {};
        }
        current = current[pathArray[i]];
      }
      
      current[pathArray[pathArray.length - 1]] = value;
      
      // Auto-enable parent section if any child is enabled
      if (value === true && pathArray.length > 1) {
        const sectionKey = pathArray[0];
        const config = permissionLabels[sectionKey];
        
        // If section has an "enabled" field, check if we should enable it
        if (config?.hasEnabled && newPermissions[sectionKey]) {
          // Check if any item in the section is now true
          const hasAnyEnabled = checkIfAnyChildEnabled(newPermissions[sectionKey]);
          if (hasAnyEnabled) {
            newPermissions[sectionKey].enabled = true;
          }
        }
      }
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  const checkIfAnyChildEnabled = (sectionObj) => {
    for (const key in sectionObj) {
      if (key === 'enabled') continue; // Skip the enabled field itself
      
      const value = sectionObj[key];
      if (typeof value === 'boolean' && value === true) {
        return true;
      } else if (typeof value === 'object' && value !== null) {
        // Recursively check nested objects
        if (checkIfAnyChildEnabled(value)) {
          return true;
        }
      }
    }
    return false;
  };

  const toggleAllInSection = (sectionKey, enabled) => {
    setFormData(prev => {
      const newPermissions = { ...prev.permissions };
      const section = newPermissions[sectionKey] || {};
      
      // Set enabled flag
      section.enabled = enabled;
      
      // Toggle all items in the section
      const config = permissionLabels[sectionKey];
      
      if (config.subSections) {
        // Handle nested subsections (like online_appointments)
        Object.keys(config.subSections).forEach(subKey => {
          if (!section[subKey]) section[subKey] = {};
          Object.keys(config.subSections[subKey].items).forEach(itemKey => {
            section[subKey][itemKey] = enabled;
          });
        });
      } else if (config.items) {
        // Handle regular items
        Object.keys(config.items).forEach(itemKey => {
          section[itemKey] = enabled;
        });
      }
      
      newPermissions[sectionKey] = section;
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  const getPermissionValue = (path) => {
    const pathArray = path.split('.');
    let current = formData.permissions;
    
    for (const key of pathArray) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return false;
      }
    }
    
    return current === true;
  };

  const isSectionDisabled = (sectionKey) => {
    // Check if section should be disabled based on feature flags
    const disabledMap = {
      'hr': !featureFlags.hr_enabled,
      'records': !featureFlags.records_enabled,
      'online_ordering': !featureFlags.online_ordering_enabled,
      'online_appointments': !featureFlags.online_appointments_enabled,
      'ai_features': !featureFlags.ai_costs_enabled,
      'system_settings': !featureFlags.system_settings_enabled
    };
    return disabledMap[sectionKey] || false;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Compact with custom color */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
          <h2 className="text-lg font-semibold text-white">
            {role ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Role Details */}
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${role ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Enter role name"
                disabled={role ? true : false}
              />
              {role && (
                <p className="text-xs text-gray-500 mt-1">Role name cannot be changed after creation</p>
              )}
            </div>
          </div>

          {/* Permissions Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-theme-blue animate-spin" />
              </div>
            ) : (
              <>
              {Object.entries(permissionLabels).map(([key, config]) => {
                const isDisabled = isSectionDisabled(key);
                return (
                <div key={key} className={`border border-gray-200 rounded-lg overflow-hidden ${isDisabled ? 'opacity-50' : ''}`}>
                  {/* Section Header */}
                  <button
                    onClick={() => !isDisabled && toggleSection(key)}
                    disabled={isDisabled}
                    className={`w-full flex items-center justify-between p-3 bg-gray-50 transition-colors ${!isDisabled ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    <div className="flex items-center space-x-3">
                      {expandedSections[key] ? (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      )}
                      <div className="text-left">
                        <h4 className="font-medium text-gray-900 text-sm">{config.label}</h4>
                      </div>
                    </div>
                    {config.hasEnabled && !isDisabled && (
                      <label
                        className="flex items-center space-x-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="text-xs text-gray-700">Enable</span>
                        <input
                          type="checkbox"
                          checked={getPermissionValue(`${key}.enabled`)}
                          onChange={(e) => toggleAllInSection(key, e.target.checked)}
                          className="w-4 h-4 text-theme-blue rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </label>
                    )}
                    {isDisabled && (
                      <span className="text-xs text-gray-500 italic">Disabled in Feature Flags</span>
                    )}
                  </button>

                  {/* Section Content */}
                  {expandedSections[key] && (
                    <div className="p-4 bg-white space-y-3">
                      {config.subSections ? (
                        // Nested sub-sections (like online_appointments)
                        Object.entries(config.subSections).map(([subKey, subConfig]) => (
                          <div key={subKey} className="ml-4 border-l-2 border-gray-200 pl-4">
                            <h5 className="font-medium text-gray-800 mb-2">{subConfig.label}</h5>
                            <div className="space-y-2">
                              {Object.entries(subConfig.items).map(([itemKey, itemLabel]) => (
                                <label key={itemKey} className="flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded">
                                  <span className="text-sm text-gray-700">{itemLabel}</span>
                                  <input
                                    type="checkbox"
                                    checked={getPermissionValue(`${key}.${subKey}.${itemKey}`)}
                                    onChange={(e) => updatePermission(`${key}.${subKey}.${itemKey}`, e.target.checked)}
                                    className="w-5 h-5 text-theme-blue rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                </label>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        // Regular items
                        <div className="space-y-2">
                          {Object.entries(config.items).map(([itemKey, itemLabel]) => (
                            <label key={itemKey} className="flex items-center justify-between py-2 hover:bg-gray-50 px-2 rounded">
                              <span className="text-sm text-gray-700">{itemLabel}</span>
                              <input
                                type="checkbox"
                                checked={getPermissionValue(`${key}.${itemKey}`)}
                                onChange={(e) => updatePermission(`${key}.${itemKey}`, e.target.checked)}
                                className="w-5 h-5 text-theme-blue rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </>
          )}
          </div>
        </div>

        {/* Footer - Compact with custom color */}
        <div className="flex justify-end px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--theme-primary)' }}
            onMouseEnter={(e) => !saving && !loading && (e.currentTarget.style.backgroundColor = 'var(--theme-primary-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-primary)')}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span className="text-sm">Save Role</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleEditorModal;
